/**
 * EditReasonDialog — Edit the reason code on an event.
 *
 * Shows current reason, a select for new reason, edit history,
 * and save/cancel buttons.
 *
 * Props:
 *   event        — { event_id, reason_code_id, reason_code_description, reason_edits }
 *   reasonCodes  — [{ reason_code_id, reason_code_description }]
 *   onClose      — () => void
 *   onSave       — (eventId, newReasonId, oldReasonId) => void
 *   saving       — boolean
 */

import { useState, useCallback } from 'react';

const EditReasonDialog = ({ event, reasonCodes, onClose, onSave, saving }) => {
    const [selectedReasonId, setSelectedReasonId] = useState('');

    const handleSave = useCallback(() => {
        if (!selectedReasonId) return;
        onSave(event.event_id, parseInt(selectedReasonId, 10), event.reason_code_id || 0);
    }, [selectedReasonId, event, onSave]);

    const isValid = selectedReasonId !== '' && parseInt(selectedReasonId, 10) !== (event.reason_code_id || 0);

    const editHistory = event?.reason_edits || [];

    return (
        <div className="pp-dialog-backdrop" onClick={onClose}>
            <div className="pp-dialog" onClick={(e) => e.stopPropagation()}>
                <div className="pp-dialog__header">
                    <h2 className="pp-dialog__title">Edit Event Reason</h2>
                    <button className="dp-drawer__close" onClick={onClose}>{'\u2715'}</button>
                </div>
                <div className="pp-dialog__body">

                    {/* Current reason */}
                    <div className="dp-drawer__field">
                        <label className="dp-drawer__label">Current Reason</label>
                        <p className="dp-cell-text" style={{ margin: 0 }}>
                            {event?.reason_code_description || 'No Reason'}
                        </p>
                    </div>

                    {/* New reason selector */}
                    <div className="dp-drawer__field">
                        <label className="dp-drawer__label">New Reason</label>
                        <select
                            className="dp-drawer__select"
                            value={selectedReasonId}
                            onChange={(e) => setSelectedReasonId(e.target.value)}
                        >
                            <option value="">Select reason…</option>
                            {(reasonCodes || []).map((rc) => (
                                <option key={rc.reason_code_id} value={rc.reason_code_id}>
                                    {rc.reason_code_description}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Edit history */}
                    {editHistory.length > 0 ? (
                        <div style={{ marginTop: 'var(--space-4)' }}>
                            <h4 style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text)', marginBottom: 'var(--space-2)' }}>
                                Edit History ({editHistory.length})
                            </h4>
                            <div className="dp-table-wrap">
                                <table className="dp-table">
                                    <thead className="dp-thead">
                                        <tr>
                                            <th className="dp-th">Previous Reason</th>
                                            <th className="dp-th">New Reason</th>
                                            <th className="dp-th">Edited By</th>
                                            <th className="dp-th">Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {editHistory.map((edit, idx) => (
                                            <tr key={idx} className="dp-tr">
                                                <td className="dp-td">
                                                    <span className="dp-cell-text">{edit.old_reason || '\u2014'}</span>
                                                </td>
                                                <td className="dp-td">
                                                    <span className="dp-cell-text">{edit.new_reason || '\u2014'}</span>
                                                </td>
                                                <td className="dp-td">
                                                    <span className="dp-cell-text">{edit.display_name || '\u2014'}</span>
                                                </td>
                                                <td className="dp-td">
                                                    <span className="dp-cell-text dp-cell-text--muted" style={{ fontSize: 'var(--font-size-xs)' }}>
                                                        {edit.create_time || '\u2014'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : null}
                </div>
                <div className="pp-dialog__footer">
                    <button className="dp-btn dp-btn--cancel" onClick={onClose} disabled={saving}>
                        Cancel
                    </button>
                    <button
                        className="dp-btn dp-btn--primary"
                        onClick={handleSave}
                        disabled={saving || !isValid}
                    >
                        {saving ? 'Saving\u2026' : 'Save Reason'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditReasonDialog;
